import { useCallback, useEffect, useState } from 'react'
import { CircleHelp } from 'lucide-react'
import OnboardingTour from '../../../../components/ERP/Onboarding/OnboardingTour'
import { useAuth } from '../../../../contexts/AuthContext'
import { ACTIONS, MODULES, usePermissions } from '../../../../hooks/usePermissions'
import { patientService } from '../../../../services/patientApi'
import { formatDay } from '../patientFormat'
import { THERAPY_ONBOARDING_STEPS, THERAPY_TOUR_ID } from './therapyOnboarding'
import TherapyActivityModal from './TherapyActivityModal'
import TherapyGoalModal from './TherapyGoalModal'
import TherapyGoals from './TherapyGoals'
import TherapyHistory from './TherapyHistory'
import TherapyPlan from './TherapyPlan'
import TherapySessionForm from './TherapySessionForm'

function TherapyTab({ patientId, archived }) {
  const { user } = useAuth()
  const { hasPermission } = usePermissions()
  const canCreateActivity = hasPermission(MODULES.PATIENTS, ACTIONS.PATIENTS_THERAPY_ACTIVITIES)
  const canCreateGoal = hasPermission(MODULES.PATIENTS, ACTIONS.PATIENTS_THERAPY_GOALS)
  const [replay, setReplay] = useState(0)
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [activityOpen, setActivityOpen] = useState(false)
  const [editingActivity, setEditingActivity] = useState(null)
  const [goalOpen, setGoalOpen] = useState(false)
  const [editingGoal, setEditingGoal] = useState(null)

  const load = useCallback(async () => {
    setError('')
    try {
      const response = await patientService.getTherapy(patientId)
      setData(response.data || { activities: [], goals: [], sessions: [] })
    } catch (err) {
      setError(err.response?.data?.message || 'Não foi possível carregar a reabilitação')
    }
  }, [patientId])

  useEffect(() => {
    setData(null)
    load()
  }, [load])

  const run = async (action) => {
    setError('')
    try {
      if (action) await action()
      await load()
    } catch (err) {
      setError(err.response?.data?.message || 'Não foi possível atualizar a reabilitação')
    }
  }

  if (!data) {
    return <p className="patients-muted">Carregando reabilitação…</p>
  }

  const activities = data.activities || []
  const goals = data.goals || []
  const sessions = data.sessions || []
  const activeCount = activities.filter((item) => item.status === 'ativa').length
  const openGoals = goals.filter((item) => item.status === 'em_andamento').length
  const latest = sessions[0]

  return (
    <div className="therapy-panel">
      {error && <p className="patients-alert-strip">{error}</p>}
      <div className="therapy-toolbar">
        <button type="button" className="patients-btn patients-btn--ghost patients-btn--compact" onClick={() => setReplay((current) => current + 1)}>
          <CircleHelp size={14} /> Como usar
        </button>
      </div>
      <div className="patients-metrics" data-onboarding="therapy-summary">
        <article className="patients-metric">
          <span className="patients-metric-label">Atividades ativas</span>
          <strong className="patients-metric-value">{activeCount}</strong>
        </article>
        <article className="patients-metric">
          <span className="patients-metric-label">Metas em andamento</span>
          <strong className="patients-metric-value">{openGoals}</strong>
        </article>
        <article className="patients-metric">
          <span className="patients-metric-label">Última sessão</span>
          <strong className="patients-metric-value">{latest ? formatDay(latest.session_date) : '—'}</strong>
        </article>
        <article className="patients-metric">
          <span className="patients-metric-label">Última dor</span>
          <strong className="patients-metric-value">
            {latest ? latest.pain_score : '—'}
            {latest?.pain_location ? <small> {latest.pain_location}</small> : null}
          </strong>
        </article>
      </div>

      <TherapyPlan
        patientId={patientId}
        activities={activities}
        archived={archived}
        canCreate={canCreateActivity}
        onCreate={() => { setEditingActivity(null); setActivityOpen(true) }}
        onEdit={(activity) => { setEditingActivity(activity); setActivityOpen(true) }}
        onChanged={run}
      />
      <TherapySessionForm
        patientId={patientId}
        activities={activities}
        sessions={sessions}
        archived={archived}
        onSaved={load}
      />
      <TherapyGoals
        patientId={patientId}
        goals={goals}
        archived={archived}
        canCreate={canCreateGoal}
        onCreate={() => { setEditingGoal(null); setGoalOpen(true) }}
        onEdit={(goal) => { setEditingGoal(goal); setGoalOpen(true) }}
        onChanged={run}
      />
      <TherapyHistory sessions={sessions} />

      <TherapyActivityModal
        isOpen={activityOpen}
        patientId={patientId}
        activity={editingActivity}
        onClose={() => setActivityOpen(false)}
        onSaved={load}
      />
      <TherapyGoalModal
        isOpen={goalOpen}
        patientId={patientId}
        goal={editingGoal}
        onClose={() => setGoalOpen(false)}
        onSaved={load}
      />
      <OnboardingTour
        tourId={THERAPY_TOUR_ID}
        steps={THERAPY_ONBOARDING_STEPS}
        userId={user?.id}
        ready
        replayToken={replay}
      />
    </div>
  )
}

export default TherapyTab
